import type { ReactNode } from "react";
import { useId } from "react";

import type { SafeInputHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import { type SelectionTileType, useSelectionTileGroupContext } from "../SelectionTileGroupContext";

import styles from "./SelectionTileItem.module.scss";

export type SelectionTileItemProps = {
  label?: ReactNode;
  value: string | number;
  name?: string;
  type?: SelectionTileType;
  description?: ReactNode;
  icon?: ReactNode;
  // TODO: Keep ReactNode until Tooltip is migrated to core-components; then replace with TooltipProps.
  tooltip?: ReactNode;
  disabled?: boolean;
  isError?: boolean;
  readOnly?: boolean;
  children?: ReactNode;
} & Omit<SafeInputHtmlAttributes, "type" | "value" | "children" | "className" | "readOnly">;

export const SelectionTileItem = ({
  label,
  value,
  name,
  type = "radio",
  description,
  icon,
  tooltip,
  disabled = false,
  isError = false,
  readOnly = false,
  children,
  ...rest
}: SelectionTileItemProps) => {
  const labelId = useId();
  const { isError: groupIsError, groupName, groupType } = useSelectionTileGroupContext();
  const resolvedIsError = isError || groupIsError;
  const resolvedName = groupName ?? name;
  const resolvedType = groupType ?? type;
  const hasLabel = label !== null && label !== undefined && label !== false && label !== "";
  const hasHeaderContent = hasLabel || description !== undefined || icon !== undefined || tooltip !== undefined;
  const hasExplicitAriaLabel = rest["aria-label"] !== undefined;
  const hasExplicitAriaLabelledBy = rest["aria-labelledby"] !== undefined;
  const resolvedAriaLabel = hasExplicitAriaLabel
    ? rest["aria-label"]
    : !hasExplicitAriaLabelledBy && !hasLabel
      ? String(value)
      : undefined;
  const resolvedAriaLabelledBy = hasExplicitAriaLabelledBy
    ? rest["aria-labelledby"]
    : !hasExplicitAriaLabel && hasLabel
      ? labelId
      : undefined;

  const input = (
    <input
      {...rest}
      type={resolvedType}
      value={value}
      name={resolvedName}
      disabled={disabled || readOnly}
      data-disabled={disabled || undefined}
      aria-label={resolvedAriaLabel}
      aria-labelledby={resolvedAriaLabelledBy}
      className={styles.control}
    />
  );

  return (
    <div
      className={classnames(styles.tile, styles.item, {
        [styles.invalid]: resolvedIsError,
        [styles.readOnly]: readOnly,
      })}
    >
      {hasHeaderContent ? (
        <>
          <label className={styles.header}>
            {input}
            {icon && (
              <div className={styles.icon} aria-hidden="true">
                {icon}
              </div>
            )}
            <div className={styles.content}>
              <div className={styles.valueLine}>
                {hasLabel && (
                  <span id={labelId} className={styles.valueLabel}>
                    {label}
                  </span>
                )}
                {tooltip && <span className={styles.tooltip}>{tooltip}</span>}
              </div>
              {description && <span className={styles.description}>{description}</span>}
            </div>
          </label>
          {children !== undefined && <div>{children}</div>}
        </>
      ) : (
        <label className={styles.headerless}>
          {input}
          {children !== undefined && <div>{children}</div>}
        </label>
      )}
    </div>
  );
};
